import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    Badge,
    Heading,
    SidePanel,
    SidePanelBody,
    SidePanelCloseButton,
    SidePanelExpandButton,
    SidePanelHeader,
    SidePanelHeaderActions,
    SidePanelHeaderMain,
    SidePanelHeaderTitle,
} from '@gitbook/spine-react';

import { useChangeRequestAgent } from '../Agent/useChangeRequestAgent';
import { AgentChat } from '../AgentChat';
import { AgentChangeRequestBlankslate } from './AgentChangeRequestBlankslate';
import { AgentChangeRequestControlButton } from './AgentChangeRequestControlButton';

/**
 * Sidepanel for the GitBook Agent in change requests.
 */
export function AgentChangeRequestSidePanel(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    currentContext: api.AIMessageContext;
    onClose: () => void;
}): React.ReactElement | null {
    const { space, changeRequest, currentContext, onClose } = props;

    const agent = useChangeRequestAgent({
        organizationId: space.organization,
        spaceId: space.id,
        changeRequestId: changeRequest.id,
    });

    return (
        <SidePanel expandable onClose={onClose}>
            <SidePanelHeader>
                <SidePanelHeaderMain>
                    <SidePanelHeaderTitle>
                        <Heading>GitBook Agent</Heading>
                        <Badge size="xsmall" kind="alpha" label="Early access" />
                    </SidePanelHeaderTitle>
                    <SidePanelHeaderActions>
                        <AgentChangeRequestControlButton
                            key="reset"
                            space={space}
                            changeRequest={changeRequest}
                            agent={agent}
                        />
                        <SidePanelExpandButton />
                        <SidePanelCloseButton />
                    </SidePanelHeaderActions>
                </SidePanelHeaderMain>
            </SidePanelHeader>
            <SidePanelBody>
                <AgentChat
                    agent={agent}
                    currentContext={currentContext}
                    onTrackPostMessage={(input) => {
                        analytics.logEvent(
                            analytics.createChangeRequestEvent(space, changeRequest.id, {
                                type: 'change_request_agent_message_post',
                                ...input,
                            })
                        );
                    }}
                    blankslate={AgentChangeRequestBlankslate}
                />
            </SidePanelBody>
        </SidePanel>
    );
}
